import { FC } from 'react'

import { Box, Link, Table, Tag, Text } from '@factorialco/deprecated-design-system'
import type { PayrollSendToBookkeeperIncidenceFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import {
  mandatoryFieldsTranslations,
  translatedIncidenceName,
} from 'modules/bookkeepers/lib/dynamicTranslations'

interface Props {
  incidence: PayrollSendToBookkeeperIncidenceFragment
}

export const IncidenceRow: FC<Props> = ({ incidence }) => {
  const { navigation } = useNavigation()
  const hasDevCompensations = hasFeature('dev_compensations')
  const link = hasDevCompensations
    ? navigation.compensations.employeeUpdates.id.fullPath({ id: incidence.id })
    : navigation.payrollBundle.incidences.id.fullPath({
        id: incidence.id,
      })

  const tagColor =
    incidence.ruleEvaluation?.validity === 'blocked'
      ? 'radical'
      : incidence.ruleEvaluation?.validity === 'warning'
      ? 'champagne'
      : 'correct'

  const statusText =
    incidence.ruleEvaluation?.validity === 'blocked'
      ? i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.stats.blocked.title')
      : incidence.ruleEvaluation?.validity === 'warning'
      ? i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.stats.warning.title')
      : i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.stats.valid.title')

  return (
    <Table.Row key={incidence.id}>
      <Table.Cell verticalAlign="middle">
        <Text.Normal size="200" whiteSpace="nowrap" color="grey1000">
          {translatedIncidenceName(incidence.name || '', incidence.customName)}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell verticalAlign="middle">
        <Text.Normal size="200" whiteSpace="nowrap" capitalize color="grey1000">
          {incidence.employee?.fullName}
        </Text.Normal>
      </Table.Cell>
      <Table.Cell verticalAlign="middle">
        <Box component="ul" style={{ listStyle: 'disc' }} paddingLeft="s16">
          {incidence.ruleEvaluation?.missingFields?.map((f) => (
            <Box style={{ display: 'list-item' }} component="li" key={f.field}>
              <Text.Normal size="200" whiteSpace="nowrap" key={f.field} color="grey1000">
                {mandatoryFieldsTranslations(f.field)}
              </Text.Normal>
            </Box>
          ))}
        </Box>
      </Table.Cell>
      <Table.Cell verticalAlign="middle">
        <Tag inline size="normal" type={tagColor}>
          <Text.Normal size="200" whiteSpace="nowrap" weight="medium" color="grey1000">
            {statusText}
          </Text.Normal>
        </Tag>
      </Table.Cell>
      <Table.Cell verticalAlign="middle" style={{ overflowWrap: 'break-word' }}>
        <Box flexDirection="row" gap="s4" alignItems="center">
          <Link.Href target="_blank" href={link} external type="solid">
            {i18n.t('bookkeepers.incidence.bulk_status_changes.send_to_bookkeeper.table.view')}
          </Link.Href>
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}
